import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '../cn';
import { Avatar } from './display';

/** One row of detail inside the menu, e.g. a team and the role held in it. */
export interface AccountDetail {
  readonly label: string;
  readonly value: string;
}

/** One action at the foot of the menu. */
export interface AccountAction {
  readonly label: string;
  readonly onSelect: () => void;
  /** `danger` renders in the destructive style — sign out, typically. */
  readonly tone?: 'default' | 'danger';
}

export interface AccountMenuProps {
  readonly name: string;
  readonly email: string;
  /** Profile picture URL, e.g. from Clerk. Initials render when absent. */
  readonly avatarUrl?: string | null | undefined;
  /** Job title, shown when present. */
  readonly title?: string | undefined;
  /** One-line standing summary shown under the name, e.g. "Team admin · HR". */
  readonly standing?: string | undefined;
  /** Badge in the open panel, e.g. the organization role. */
  readonly badge?: string | undefined;
  /** Detail rows in the open panel, e.g. each team and the role held there. */
  readonly details?: readonly AccountDetail[] | undefined;
  readonly actions: readonly AccountAction[];
  /** Rendered on the dark sidebar rather than the white top bar. */
  readonly variant?: 'sidebar' | 'topbar';
}

/**
 * The signed-in person's avatar, and what they can do about their own account.
 *
 * Identity actions are injected rather than owned: this package must not
 * depend on Clerk, and which actions exist ("Manage your profile", "Sign out",
 * "Switch account") is the application's decision. What the component owns is
 * the behavior that is easy to get wrong everywhere — outside-click and
 * Escape dismissal, focus return, and readable colors on both the dark
 * sidebar and the white top bar.
 *
 * @param props - Who is signed in and what they may do.
 * @returns The avatar button and its menu.
 */
export function AccountMenu({
  name,
  email,
  avatarUrl,
  title,
  standing,
  badge,
  details,
  actions,
  variant = 'sidebar',
}: AccountMenuProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click and on Escape. Escape also returns focus to the
  // trigger, so keyboard users are not dropped at the top of the document.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const onSidebar = variant === 'sidebar';

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          'flex w-full items-center gap-[9px] rounded-portal-button p-1.5 text-left transition-colors',
          onSidebar ? 'hover:bg-white/10' : 'hover:bg-portal-bg',
        )}
      >
        <Avatar name={name} size={33} imageUrl={avatarUrl} />
        {/* On the phone top bar the trigger is the avatar alone — the shell
            gives it a 46px slot, which the name and chevron cannot fit. */}
        <span className={cn('min-w-0 flex-1', !onSidebar && 'hidden min-[720px]:block')}>
          <strong className={cn('block truncate text-portal-micro', onSidebar && 'text-white')}>
            {name}
          </strong>
          {standing === undefined ? null : (
            <small
              className={cn(
                'block truncate text-[8px]',
                onSidebar ? 'text-white/60' : 'text-portal-muted',
              )}
            >
              {standing}
            </small>
          )}
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            'size-3.5 shrink-0 transition-transform',
            open && 'rotate-180',
            onSidebar ? 'text-white/60' : 'text-portal-muted',
            !onSidebar && 'hidden min-[720px]:block',
          )}
        />
        <span className="sr-only">Account menu for {name}</span>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className={cn(
            'absolute z-20 w-[248px] overflow-hidden rounded-portal-card border border-portal-line bg-white shadow-portal-card',
            // Explicit ink, not inherited: on the sidebar the container sets
            // `text-white`, and a white-on-white menu vanishes entirely.
            'text-portal-ink',
            onSidebar ? 'bottom-full left-0 mb-2' : 'top-full right-0 mt-2',
          )}
        >
          <div className="border-b border-portal-line px-3.5 py-3">
            <strong className="block truncate text-portal-small">{name}</strong>
            <span className="mt-0.5 block truncate text-portal-micro text-portal-muted">
              {email}
            </span>
            {title === undefined || title.length === 0 ? null : (
              <span className="mt-1.5 block truncate text-portal-micro">{title}</span>
            )}
            {badge === undefined ? null : (
              <span className="mt-2 inline-block rounded-portal-pill bg-brand-tint px-2 py-0.5 text-portal-micro text-brand">
                {badge}
              </span>
            )}
            {details === undefined || details.length === 0 ? null : (
              <ul className="mt-2 space-y-1">
                {details.map((detail) => (
                  <li
                    key={`${detail.label}:${detail.value}`}
                    className="flex items-baseline justify-between gap-2"
                  >
                    <span className="truncate text-portal-micro">{detail.label}</span>
                    <span className="shrink-0 text-portal-micro text-portal-muted">
                      {detail.value}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {actions.map((action, index) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                action.onSelect();
              }}
              className={cn(
                'block w-full px-3.5 py-2.5 text-left text-portal-small',
                index > 0 && 'border-t border-portal-line',
                action.tone === 'danger'
                  ? 'text-rose-700 hover:bg-rose-50'
                  : 'hover:bg-portal-bg',
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
